import { FC, memo, useRef } from 'react';
import cn from 'classnames';
import type {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import styles from './styles.module.css';
import tableDefaults from '../../shared/antTableDefaults.module.css';
import { ColumnsType } from 'antd/lib/table';
import {
  BlacklistCurrent,
  BlacklistTablet,
} from '../../../models/Blacklist/Blacklist';
import { useTableHeight } from '../../../hooks/components/useTableHeight';
import { BlacklistRequest } from '../../../store/reducers/BlacklistSlice';
import { TableFooter } from '../../TableFooter';
import { LoadingTable } from '../../shared/Loading';
import { TableResults } from '../../shared/TableResults';

interface Props {
  pageSize: number;
  total: number;
  callRequest: BlacklistRequest;
  onChange: (page: number, pageItemCount: number) => void;
  allBlacklist: BlacklistCurrent[];
  divider: { height: number; backgroundColor: string; margin: string };
  COLUMNS: ColumnsType<BlacklistTablet>;
  onTableChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<BlacklistTablet> | SorterResult<BlacklistTablet>[],
    extra: TableCurrentDataSource<BlacklistTablet>,
  ) => void;
  handlePageSizeChange: (value: string) => void;
  loading?: boolean;
  hasLoadError?: boolean;
}

export const BlacklistTable: FC<Props> = memo(
  ({
    allBlacklist,
    COLUMNS,
    divider,
    callRequest,
    pageSize,
    total,
    onChange,
    onTableChange,
    handlePageSizeChange,
    loading = false,
    hasLoadError = false,
  }: Props) => {
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    const { tableHeight, containerHeight, isTabletScroll } = useTableHeight({
      tableContainerRef,
      items: allBlacklist,
    });

    const tabletScroll = isTabletScroll ? { y: tableHeight } : undefined;

    return (
      <div ref={tableContainerRef} className={styles.BlacklistTable}>
        <TableResults
          isEmpty={!hasLoadError && allBlacklist.length === 0 && total === 0}
          loading={loading}
        >
          <div style={{ height: `${containerHeight}px` }}>
            <LoadingTable<BlacklistTablet>
              className={cn(
                styles.table,
                styles.stickyHeader,
                tableDefaults.defaultHeader,
                tableDefaults.defaultRowGray,
                tableDefaults.defaultPlusCell,
              )}
              style={{ tableLayout: 'auto' }}
              dataSource={allBlacklist.map((item, index) => ({
                ...item,
                index,
              }))}
              columns={COLUMNS}
              onChange={onTableChange}
              pagination={false}
              showSorterTooltip={false}
              rowKey={(record) => record.id}
              scroll={tabletScroll}
              loading={loading}
            />
          </div>
          <div className={styles.divider} style={divider} />
          <TableFooter
            pageIndex={callRequest.PageIndex}
            pageSize={pageSize}
            total={total}
            onPageChange={onChange}
            onPageSizeChange={handlePageSizeChange}
            disabled={loading}
          />
        </TableResults>
      </div>
    );
  },
);

BlacklistTable.displayName = 'BlacklistTable';
